import type { Periods } from '@app/src/components/CreateNewRequestModal/PeriodSelector/PeriodSelector'
import type {
  CategoryOption,
  FinalRiskRating,
  ProviderActivityStatus,
  ProviderPriority,
  SupplierUsage,
} from '@app/src/types/categories'
import type { MappingNode } from '@app/src/types/product'
import type {
  Contact,
  Country,
  CountryVariant,
  Image,
  ProviderRisk,
  ReferralContact,
  ResourceType,
  Risk,
  RiskSource,
  User,
} from '@app/src/types/resourceExplorer'
import type {
  OrganizationSettings,
  ProviderType,
  ResourceTypes,
  Solutions,
  SpendClassification,
} from '@app/src/wf-constants'
import type Assessment from './assessment'

export enum ProviderApprovalStatus {
  APPROVED = 'Approved',
  UNDER_OBSERVATION = 'UnderObservation',
  APPROVED_AFTER_CONSIDERATION = 'ApprovedAfterConsideration',
  NOT_APPROVED = 'NotApproved',
  NOT_SELECTED = 'NotSelected',
}

export enum ProviderActivityType {
  CHANGE_RISK_ASSESSMENT = 'ChangeRiskAssessment',
  CHANGE_APPROVAL_STATUS = 'ChangeApprovalStatus',
  CHANGE_BASELINE_ASSESSMENT_LEVEL = 'ChangeBaselineAssessmentLevel',
  CHANGE_AUTOMATED_ASSESSMENT_PERCENTAGE = 'ChangeAutomatedAssessmentPercentage',
}

export enum ProviderEnrichmentStatus {
  InProgress = 'InProgress',
  Completed = 'Completed',
  Failed = 'Failed',
}

export interface ProviderEnrichment {
  id: number
  providerId: number
  status: ProviderEnrichmentStatus
  createdAt: string
  completedAt?: string
  errorMessage?: string
  durationMs?: number
  industryId?: number
  industry?: NaceCode
  subIndustryId?: number
  subIndustry?: NaceCode
  companySizeRange?: string
  revenueRange?: string
  registrationNumber?: string
  vatNumber?: string
  websiteAddress?: string
  websiteDomain?: string
}

export interface SimpleOrganization extends ResourceType {
  id: number
  createdAt: string
  name: string
  registrationNumber: string
  vatNumber: string
  glnNumber: string
  websiteAddress: string
  primaryAddress: string
  address?: string
  image: Image
  imageId?: number
  creatorOrganizationId?: number
  creatorUserId?: number
  type?: string
  countryId?: number
  industryNaceCode?: NaceCode
  industryId?: number
  subIndustryNaceCode?: NaceCode
  subIndustryId?: number
  companySizeRange?: CompanyRanges
  revenueRange?: CompanyRanges
  organizationSettings?: OrganizationSettings[]
  setting?: OrganizationSettings
  actualUsers?: number
  actualSuppliers?: number
}

export interface AssignedSolution {
  accountType: string
  configuration: { templateIds: number[]; indicatorIds: number[] }
  createdAt: string
  creatorOrganizationId: number
  creatorUserId: number
  id: number
  organization: Partial<Organization>
  organizationId: number
  solutionType: string
}

export interface UsersOrganization {
  organizationId: number
  solutions: { type: string; role: string; isOwnerRestricted: boolean }[]
}

export interface OrganizationCountriesExposure {
  country: Country
  variant: CountryVariant.Operation | CountryVariant.Subsupplier
}

export interface Spend {
  id: number
  amount: number
  percentage: number
  periodName: string
  periodType: Periods
  spendClassification?: SpendClassification
}

export interface Organization extends SimpleOrganization {
  country: Country
  locations?: Location[]
  assignedSolutions?: AssignedSolution[]
  organizationsCountriesExposure: OrganizationCountriesExposure[]
}

export interface Provider extends Organization {
  customId?: string
  targetOrganizationId: number
  organization: GenericOrganization
  linked: boolean
  linkStatus: ProviderLinkStatus
  deletedAt?: string
  contacts?: Contact[]
  ownerUsers?: User[]
  ownerUserIds?: number[]
  organizationId: number
  blockedDomains?: string[]
  type: ProviderType
  providerApprovalStatus?: ProviderApprovalStatus
  providerApprovalComment?: string
  providerApprovalSetAt?: string
  alreadyLinkedOrganization?: boolean
  categoryOptions?: CategoryOption[]
  categoryOptionIds?: number[]
  activityStatus?: ProviderActivityStatus
  finalRiskRating?: FinalRiskRating
  priority?: ProviderPriority
  supplierUsage?: SupplierUsage
  tier?: number
  riskPrioritization?: ProviderRisk[]
  referralContact?: ReferralContact
  duns?: string
  assessments?: Assessment[]
  spends?: Spend[]
  mappingNodes?: MappingNode[]
  enrichments?: ProviderEnrichment[]
  // Provider-level industry (different from org's industryNaceCode/subIndustryNaceCode)
  industry?: NaceCode
  subIndustry?: NaceCode
}

export interface ProviderActivity {
  id: number
  creatorUser: User
  createdAt: string
  activityType: ProviderActivityType
  comments?: Comment[]
  to: string
  from: string
}

export interface UpdateProviderFinalRiskRating {
  providerIds: number[]
  comment: string
  newFinalRiskRating: FinalRiskRating
}

export interface Comment {
  text: string
  id: number
}

export interface Investee extends Provider {
  type: typeof ResourceTypes.Investee
}

export interface Supplier extends Provider {
  type: typeof ResourceTypes.Supplier
}

export interface Company extends Provider {
  type: typeof ResourceTypes.Company
}

export interface RiskTableView {
  id: number
  parentObject: GenericOrganization
  risks: RiskSource[]
}

export type GenericOrganization = Investee | Supplier | Company

export interface NaceCode {
  level: number
  id: number
  parentId: number
  parentDescription?: string
  description: string
  code: string
  items?: NaceCode[]
  risks: Risk[]
}

export interface Location {
  address?: string
  createdAt: string
  creatorOrganizationId: number
  creatorUserId: number
  id: number
  lat?: number
  lng?: number
  latitude?: number
  longitude?: number
  objectType: string
  targetObjectId: number
  type: string
  formattedAddress?: string
  region?: string
  subRegion?: string
  city?: string
  district?: string
  countryId: number
}

export interface ClaimLocation {
  address: string
  country: string
  region: string
  subRegion: string
  city: string
  district: string
  lat: number
  lng: number
}

export enum CompanyRanges {
  MICRO = 'micro',
  SMALL = 'small',
  MEDIUM = 'medium',
  LARGE = 'large',
  GIANT = 'giant',
}

export interface ClaimDetails {
  name: string
  websiteAddress?: string
  industryId?: number
  subIndustryId?: number
  tags?: string
  address?: string
  location: Partial<ClaimLocation>
  country?: string
  vatNumber: string
  logo: string
  referralCode?: string
  referralCompanyName?: string
  registrationNumber?: string
  companySizeRange?: CompanyRanges
  revenueRange?: CompanyRanges
}
export interface Claim {
  id: number
  details: ClaimDetails
  externalSourceObject?: unknown
  claimStatus?: string
  objectType?: string
  creatorOrganization?: Organization
  creatorUser?: User
  createdAt?: string
  creatorOrganizationId?: number
  creatorUserId?: number
  type?: string
  reviewReason?: string
}

export interface ClaimUpdate {
  claimIds: number[]
  claimStatus: string
}

export interface ScheduledRequests {
  templateId: number
  periodStartsAt: string
  periodEndsAt: string
  dueAt: string
}

export interface ReferralProviderDetails {
  name?: string
  country?: Country
  websiteAddress?: string
  vatNumber?: string
  registrationNumber?: string
  industryId?: number
  industry?: NaceCode
  subIndustryId?: number
  subIndustry?: NaceCode
  companySizeRange?: string
  revenueRange?: string
}

export interface ReferralCode {
  id: number
  creatorOrganizationName: string
  creatorOrganizationImageUrl: string
  code: string
  creatorOrganizationId: number
  solutionType: keyof typeof Solutions
  scheduledRequests?: ScheduledRequests[]
  country?: Country
  providerId?: number
  providerDetails?: ReferralProviderDetails
}

export interface ProviderOrganizationResult {
  providerId: number
  organizations: Organization[]
}

export interface ProviderOrganizationSimilarityResult {
  providerId: number
  organizations: OrganizationSimilarityResult[]
}

export interface OrganizationSimilarityResult {
  nameSimilarity: number
  vatNumberSimilarity: number
  registrationNumberSimilarity: number
  websiteAddressSimilarity: number
  overallSimilarity: number
  organization: Organization
}

export enum ProviderLinkStatus {
  Created = 'Created',
  Connected = 'Connected',
  Invited = 'Invited',
}

export enum MatchType {
  ExactMatch = 'ExactMatch',
  ApproximateMatch = 'ApproximateMatch',
}

export interface OrganizationDuplicateMatch {
  matchType: MatchType
  organization: Organization
}

export function isProvider(item: Organization | Provider): item is Provider {
  return typeof (item as Provider).linked !== 'undefined'
}
